import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Button } from "@mui/material";
import remarkGfm from "remark-gfm";
import { Flex } from "@100mslive/roomkit-react";
import { UserIcon } from "../../../icons";
import formatDateTime from "../../Common/formatDateTime";
import SEO from "../../Common/SEO";
import FullPageProgress from "../../FullPageProgress";
import { ToastManager } from "../../Toast/ToastManager";
import CompanyChatEditor from "./CompanyChatEditor";
import { SESSION_AUTH } from "../../../common/auth";
import { MESSAGE_SERVICES, REALTIME_SERVICES } from "../../../services";
import "./style.scss";

const CompanyChat = props => {
  const { orgId, orgName, handleLogin } = props;
  const { isUserSession, LOGOUT_USER_SESSION } = SESSION_AUTH();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [queryMsg, setQueryMsg] = useState("");
  const [messageList, setMessageList] = useState([]);
  const [userData, setUserData] = useState("");
  const dataFetched = useRef(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (isUserSession) {
      setUserData(isUserSession);
    } else {
      setUserData("");
    }
  }, [isUserSession]);

  useEffect(() => {
    if (!orgId || !userData) return;

    const realtime = REALTIME_SERVICES.connect("/community", realtime => {
      realtime.emit("joinCommunity", { accountId: orgId });
    });

    realtime.on("newCommunityMsg", data => {
      if (data?.code === 200) {
        updateData([data.data]);
      }
    });

    return () => {
      REALTIME_SERVICES.disconnect("/community");
    };
  }, [orgId, userData]);

  useEffect(() => {
    if (!orgId || dataFetched.current) return;
    dataFetched.current = true;
    if (page > 1 && !userData) {
      return handleLogin();
    }
    setLoading(true);
    if (page === 1) {
      setMessageList([]);
    }
    MESSAGE_SERVICES.community({
      userId: userData?.userId,
      org: userData?.org?.id,
      id: orgId,
      page: page,
    })
      .then(data => {
        if (data.code === 200) {
          updateData(data.data?.list);
          setHasMore(data?.data?.hasMore);
        } else if (data.code === 600) {
          LOGOUT_USER_SESSION();
          ToastManager.addToast({ title: data.message });
        } else {
          ToastManager.addToast({ title: data.message });
        }
      })
      .catch(err => {
        ToastManager.addToast({ title: err.message });
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LOGOUT_USER_SESSION, orgId, userData, dataFetched, page]);

  const updateData = (data, nmsg = false) => {
    if (data && data?.length > 0) {
      setMessageList(old => {
        const merged = nmsg ? [...old, ...data] : [...data, ...old];
        const sorted = merged.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        return sorted;
      });
    }
  };

  const loadmore = () => {
    dataFetched.current = false;
    setPage(old => old + 1);
  };

  const sendMessage = async () => {
    try {
      let q = queryMsg.trim();
      if (!q) return;
      if (!userData) {
        return handleLogin();
      }
      const realtime = REALTIME_SERVICES.getRealtime("/community");
      if (!realtime || realtime?.disconnected) {
        ToastManager.addToast({
          title: "Network error",
        });
        return;
      }
      setSending(true);
      let parma = {
        userId: userData?.userId,
        org: userData?.org?.id,
        accountId: orgId,
        message: q,
      };

      realtime.emit("sendCommunityMsg", parma, res => {
        if (res.code === 200) {
          setQueryMsg("");
        } else if (res.code === 600) {
          LOGOUT_USER_SESSION();
          ToastManager.addToast({ title: res.message });
        } else {
          ToastManager.addToast({ title: res.message });
        }
        setSending(false);
      });
    } catch (err) {
      ToastManager.addToast({ title: err.message });
      setSending(false);
    }
  };

  const ChatmessageUser = (message, key) => {
    return (
      <Flex
        className={`event-page-company-chat-messages-row  ${
          message?.sender?._id === userData?.userId
            ? "event-page-company-chat-messages-row-right"
            : ""
        } `}
        key={key}
      >
        <div className="event-page-company-chat-messages-row-logo">
          {message?.sender?.profileImage ? (
            <img src={message?.sender?.profileImage} alt="user" />
          ) : (
            <UserIcon />
          )}
        </div>
        <div className="event-page-company-chat-messages-row-content">
          <div className="event-page-company-chat-messages-row-content-top">
            {message?.sender?.name}
          </div>
          <div className="event-page-company-chat-messages-row-content-text">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message?.message}
            </ReactMarkdown>
          </div>
          <div className="event-page-company-chat-messages-row-content-date">
            {formatDateTime(message?.createdAt)}
          </div>
        </div>
      </Flex>
    );
  };

  return (
    <React.Fragment>
      <SEO title={`${orgName || "Profile"} | Chat`} />
      <Flex className="event-page-company-chat">
        <Flex className="event-page-company-chat-messages">
          {loading ? (
            <FullPageProgress fixed={hasMore ? false : true} />
          ) : hasMore ? (
            <div className="text-center">
              <Button
                size="small"
                variant="outlined"
                className="events6ix-btn events6ix-btn-light events6ix-btn-sm"
                type="button"
                sx={{ textTransform: "none" }}
                onClick={loadmore}
              >
                Load previous
              </Button>
            </div>
          ) : null}

          {messageList.map((item, key) => ChatmessageUser(item, key))}
        </Flex>
        <Flex className="event-page-company-chat-bottom">
          <CompanyChatEditor
            sendMessage={sendMessage}
            sending={sending}
            queryMsg={queryMsg}
            setQueryMsg={setQueryMsg}
          />
        </Flex>
      </Flex>
    </React.Fragment>
  );
};

export default CompanyChat;
